import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/system';

const SnackbarContext = createContext();

const CustomSnackbar = styled(Snackbar)(({ theme }) => ({
    /* -------------- root ( .MuiSnackbar-root ) ---------------- */
    zIndex: 2100,
    position: 'fixed', //will not scroll
    top: 80,
    right: 32,
    /* mobile */
    [theme.breakpoints.down('sm')]: {
        top: 'calc(env(safe-area-inset-top, 0px) + 16px)', // 16 for iphone notch
        left: '50%',
        transform: 'translateX(-50%)',
        right: 'auto',
        width: 'calc(100% - 32px)',
    },

    /* -------------- inside the message ------------------------------ */
    '& .MuiPaper-root': {
        width: '100%',
        height: 'auto',
        padding: 12,
        borderRadius: 8,
    },
}));

const CustomAlert = styled(Alert)(({ theme, severity }) => ({
    width: '100%',
    height: '100%',
    backgroundColor:
        severity === 'error'
            ? '#f44336'
            : severity === 'warning'
              ? '#ff9800'
              : severity === 'info'
                ? '#2196f3'
                : severity === 'success'
                  ? '#4caf50'
                  : '#616161',
    color: theme.palette.common.white,
    '& .MuiAlert-icon': {
        display: 'none',
    },
}));

export const SnackbarProvider = ({ children }) => {
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success', duration: 6000 });

    const showSnackbar = useCallback((message, severity = 'success', duration = 6000) => {
        setSnackbar({ open: true, message, severity, duration });
    }, []);

    const handleClose = useCallback(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    }, []);

    return (
        <SnackbarContext.Provider value={showSnackbar}>
            {children}
            <CustomSnackbar
                container={() => document.body}
                open={snackbar.open}
                autoHideDuration={snackbar.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <CustomAlert onClose={handleClose} severity={snackbar.severity}>
                    {snackbar.message}
                </CustomAlert>
            </CustomSnackbar>
        </SnackbarContext.Provider>
    );
};

export const useSnackbar = () => {
    return useContext(SnackbarContext);
};
